import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import css from './BlogPostPage.module.css';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { Page, NamedLink } from '../../components';
import { getBlogPostBySlug } from '../../util/contentful';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const blogPost = await getBlogPostBySlug(slug);
        if (blogPost) {
          setPost(blogPost);
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        console.error('Error loading blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Rich text rendering options
  const renderOptions = {
    renderNode: {
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { file, title } = node.data.target.fields;
        return (
          <img 
            src={`https:${file.url}`} 
            alt={title || 'Blog image'} 
            className={css.contentImage}
          />
        );
      },
      [BLOCKS.HEADING_1]: (node, children) => (
        <h2 className={css.contentH1}>{children}</h2>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className={css.contentH2}>{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className={css.contentH3}>{children}</h3>
      ),
      [BLOCKS.PARAGRAPH]: (node, children) => (
        <p className={css.contentParagraph}>{children}</p>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <a 
          href={node.data.uri} 
          target="_blank" 
          rel="noopener noreferrer"
          className={css.contentLink}
        >
          {children}
        </a>
      ),
    },
  };

  if (loading) {
    return (
      <Page>
        <div className={css.pageWrap}>
          <TopbarContainer />
          <div className={css.loadingContainer}>
            <p>Loading blog post...</p>
          </div>
          <FooterContainer />
        </div>
      </Page>
    );
  }

  if (error || !post) {
    return (
      <Page>
        <div className={css.pageWrap}>
          <TopbarContainer />
          <div className={css.errorContainer}>
            <h2>Blog Post Not Found</h2>
            <p>{error || 'The blog post you are looking for does not exist.'}</p>
            <NamedLink name="BlogPage" className={css.backLink}>
              ← Back to Blog
            </NamedLink>
          </div>
          <FooterContainer />
        </div>
      </Page>
    );
  }

  return (
    <Page 
      title={post.title}
      description={post.subtitle}
    >
      <div className={css.pageWrap}>
        <TopbarContainer />
        <div className={css.blogPostContainer}>
          <div className={css.backLinkContainer}>
            <NamedLink name="BlogPage" className={css.backLink}>
              ← Back to Blog
            </NamedLink>
          </div>

          {post.featuredImage && (
            <div className={css.featuredImageContainer}>
              <img 
                src={`https:${post.featuredImage}`} 
                alt={post.title}
                className={css.featuredImage}
              />
            </div>
          )}

          <article className={css.articleContainer}>
            <header className={css.articleHeader}>
              <h1 className={css.title}>{post.title}</h1>
              {post.subtitle && (
                <p className={css.subtitle}>{post.subtitle}</p>
              )}
              <div className={css.meta}>
                {post.author?.name && (
                  <span className={css.author}>By {post.author.name}</span>
                )}
                {post.publishedDate && (
                  <span className={css.date}>
                    {formatDate(post.publishedDate)}
                  </span>
                )}
              </div>
            </header>

            <div className={css.content}>
              {post.content && documentToReactComponents(post.content, renderOptions)}
            </div>
          </article>
        </div>
        <FooterContainer />
      </div>
    </Page>
  );
};

export default BlogPostPage;